import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    deleteUsers,
    addUsers,
    modifyUserRoles,
    changeUserStatus,
    resetUserPassword,
} from "../../services/user-management";
import { updateDefaultPractice } from "../../services/user";
import { IApiResponse } from "../../utils/types/api-response";
import {
    IChangeStatus,
    IInternalUser,
    IModifyRoles,
    INewUsers,
} from "../../utils/types/user";

// Hook for deleting users
export function useDeleteUsers() {
    interface ApiResponse {
        message: string;
    }

    return useMutation<ApiResponse, AxiosError<ApiResponse>, string[]>({
        mutationKey: ["delete-users"],
        mutationFn: (userIds: string[]) => deleteUsers(userIds),
    });
}

// Hook for adding new users
export function useAddUsers() {
    interface AddusersApiResponse<DataType> extends IApiResponse<DataType> {
        practice_domains: string[];
    }
    return useMutation<
        AddusersApiResponse<IInternalUser>,
        AxiosError<AddusersApiResponse<IInternalUser>>,
        INewUsers
    >({
        mutationKey: ["add-users"],
        mutationFn: (data: INewUsers) => addUsers(data),
    });
}

export function useChangeUserStatus() {
    return useMutation<
        IApiResponse<IInternalUser>,
        AxiosError<IApiResponse<IInternalUser>>,
        IChangeStatus
    >({
        mutationKey: ["modify-roles"],
        mutationFn: (data: IChangeStatus) => changeUserStatus(data),
    });
}

export function useModifyUserRoles() {
    return useMutation<
        IApiResponse<IInternalUser>,
        AxiosError<IApiResponse<IInternalUser>>,
        IModifyRoles
    >({
        mutationKey: ["modify-roles"],
        mutationFn: (data: IModifyRoles) => modifyUserRoles(data),
    });
}

export function useUpdateDefaultPractice() {
    interface ApiResponse {
        message: string;
    }

    return useMutation<ApiResponse, AxiosError<ApiResponse>, string | null>({
        mutationKey: ["update-default-practice"],
        mutationFn: (defaultPracticeId) =>
            updateDefaultPractice(defaultPracticeId),
    });
}

export function useResetPassword() {
    interface ApiResponse {
        message: string;
    }
    return useMutation<void, AxiosError<ApiResponse>, string[]>({
        mutationKey: ["reset-password"],
        mutationFn: (emails: string[]) => resetUserPassword(emails),
    });
}
